import * as Yup from 'yup';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import React, { useState, forwardRef, useEffect, useCallback, CSSProperties } from 'react';
import { useDropzone } from 'react-dropzone';

import {
    Box,
    Grid,
    Typography,
    Divider,
    TextField,
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    Select,
    CircularProgress,
    Zoom,
    Tooltip
} from '@mui/material';
import DateTimePicker from '@mui/lab/DateTimePicker';
import DetailsIcon from '@mui/icons-material/Details';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';

import { useSnackbar } from 'notistack';

import SaveIcon from '@mui/icons-material/Save';

import useRefMounted from '../../hooks/useRefMounted';
import { updateEvent } from '../../slices/eventSlice';
import { TextEditor } from '../TextEditor';
import Text from '../Text';
import axios from '../../utils/axios';
import { CreateTicketDto } from '../../dto/ticket/create-ticket.dto';
import { UpdateTicketDto } from '../../dto/ticket/update-ticket.dto';
import { TransitionProps } from '@mui/material/transitions';
import { CategoryResponse } from '../../dto/category/category-response.dto';
import { SubCategoryResponse } from '../../dto/subcategory/subcategory-response.dto';
import { ClassificationResponse } from '../../dto/classification/classification-response.dto';
import { LIST_COMUMNES, VALIDATION_RANGE_COMMUNE } from '../../constants';

interface IFormEvent {
    data: any;
    titleButtonAction: string;
    isEdit: boolean;
    messageOK?: string;
    showButtonAttachment?: boolean;
}

const thumbsContainer = {
    display: 'flex',
    flexDirection: 'row',
};

const thumbInner = {
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden'
};

const img = {
    display: 'block',
    width: '200px',
    height: '200px'
};

const dropzone = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: '2px',
    borderRadius: '2px',
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out'
}

const FormEvent: React.FC<IFormEvent> = ({ data, titleButtonAction, isEdit, messageOK = 'The event was created successfully', showButtonAttachment = true }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const isMountedRef = useRefMounted();

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubCategories] = useState([]);
    const [classifications, setClassifications] = useState([]);

    const [category, setCategory] = useState(0);
    const [subcategory, setSubcategory] = useState(0);

    const isClosed = data.statusCode == 'CERRADO' ? true : false;

    const [files, setFiles] = useState([]);
    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop: acceptedFiles => {
            setFiles(acceptedFiles.map(file => Object.assign(file, {
                preview: URL.createObjectURL(file)
            })));
        },
        noClick: true,
        noKeyboard: true,
        maxFiles: 3
    });

    const thumbs = files.map(file => (
        <div style={{
            display: 'inline-flex',
            borderRadius: 2,
            border: '1px solid #eaeaea',
            padding: 4,
            margin: '0 auto',
            boxSizing: 'border-box'
        }} key={file.name}>
            <div style={thumbInner}>
                <img
                    src={file.preview}
                    style={img}
                    onLoad={() => { URL.revokeObjectURL(file.preview) }}
                />
            </div>
        </div>
    ));

    const getCategories = useCallback(async () => {
        try {
            const response = await axios.get('/api/ticket/category');

            if (isMountedRef.current) {
                setCategories(response.data.payload);
            }

        } catch (err) {
            console.error('listCategoriesException', err);
        }
    }, [isMountedRef])

    const getSubCategories = async () => {
        try {
            const response = await axios.get('/api/ticket/category/' + category + '/subcategories');

            setSubCategories(response.data.payload);

        } catch (err) {
            console.error('listSubCategoriesException', err);
        }
    }

    const getClassifications = async () => {
        try {
            const response = await axios.get('/api/ticket/category/subcategories/' + subcategory + '/classifications');

            setClassifications(response.data.payload);

        } catch (err) {
            console.error('listClassificationsException', err);
        }
    }

    const createAttachment = async (ticketId: number | string, files: File[]) => {
        let wasCreated = false;

        try {
            const formData = new FormData();
            for (let i = 0; i < files.length; i++) {
                formData.append("files", files[i]);
            }

            const response = await axios.post(`/api/ticket/${ticketId}/attachment/bulk`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });


            if (response.status == 200 && !response.data.hasOwnProperty('errors')) {
                wasCreated = true;
                handleCreateEventMessages('The file was uploaded successfully', 'success');
            } else {
                throw response.data;
            }
        } catch (error) {
            console.error('ExceptionFileAttachment', error);
            handleCreateEventMessages('An error occurred while attaching the file. Please contact the administrator', 'error');
        }

        return wasCreated;
    }

    const createEvent = async (data: CreateTicketDto) => {
        try {
            const response = await axios.post('/api/ticket', {
                "description": data.description,
                "kilometer": data.kilometer,
                "sector": data.sector,
                "commune": data.commune,
                "orientation": data.orientation,
                "dateEvent": data.dateEvent,
                "channelCode": data.channelCode,
                "classificationId": data.classificationId,
                "realAddress": data.realAddress,
                "email": data.email,
                "fullName": data.fullName,
                "phone": data.phone,
                "rut": data.rut,
                "patente": data.patente,
            });


            if (response.status == 200) {

                if (response.data.hasOwnProperty('payload')) {
                    if (data.file == null) {
                        handleCreateEventMessages(messageOK, 'success')
                    } else {

                        let wasCreatedAttachment = await createAttachment(response.data.payload, data.file);

                        if (wasCreatedAttachment) {
                            handleCreateEventMessages(messageOK, 'success')
                        } else {
                            console.error('ErrorExceptionServiceAttachment', wasCreatedAttachment);
                            handleCreateEventMessages('The event was created, but the attachment could not be loaded. If you want to try again, edit the event and upload the attachment', 'warning')
                        }
                    }
                } else if (response.data.hasOwnProperty('errors')) {
                    console.error('ErrorsCreateEvent', response.data.errors);
                    handleCreateEventMessages(t('error_internal_event'), 'error')
                }
            }
        } catch (error) {
            console.error('ExceptionError', error);
            handleCreateEventMessages(t('error_internal_event'), 'error')
        }

    }

    const editEvent = async (data: any) => {
        const response = await updateEvent(data);

        if (response.status == 200 && response.data.payload !== null) {
            if (data.file == null) {
                handleCreateEventMessages('The event was updated successfully', 'success');
            } else {
                let wasCreatedAttachment = await createAttachment(data.id, data.file);

                if (wasCreatedAttachment) {
                    handleCreateEventMessages('The event was created successfully', 'success')
                } else {
                    console.error('ErrorExceptionServiceAttachment', wasCreatedAttachment);
                    handleCreateEventMessages('the event was created, but the attachment could not be loaded. If you want to try again, edit the event and upload the attachment', 'warning')
                }
            }
        }
    }

    const closedTicket = async (body: UpdateTicketDto) => {

        body.statusCode = 'CERRADO';

        const res = await updateEvent(body);

        if (res) {
            handleCreateEventMessages('The ticket was closed successfully', 'success')
        } else {
            handleCreateEventMessages('The ticket was not closed', 'error')
        }
    }

    const handleCreateEventMessages = (message: string, variant: any) => {
        enqueueSnackbar(t(message), {
            variant: variant,
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
            },
            TransitionComponent: Zoom as React.ComponentType<TransitionProps>
        });
    };

    useEffect(() => {
        getCategories();

        if (isEdit) {
            setCategory(data.category)
        }
    }, [getCategories])

    useEffect(() => {

        if (category !== 0) {
            setSubCategories([]);
            setClassifications([]);
            getSubCategories();
        }

        if (isEdit) {
            setSubcategory(data.subcategory)
        }
    }, [category])

    useEffect(() => {

        if (subcategory !== 0) {
            setClassifications([]);
            getClassifications();
        }

    }, [subcategory])

    useEffect(() => {
        return () => files.forEach(file => URL.revokeObjectURL(file.preview));
    }, [files]);

    if (categories.length !== 0) {
        return (
            <Formik
                initialValues={{
                    id: data.id,
                    description: data.description,
                    kilometer: data.kilometer,
                    sector: data.sector,
                    commune: data.commune,
                    orientation: data.orientation,
                    channelCode: 'WEB_SELF_SERVICE',
                    classificationId: data.classificationId,
                    dateEvent: data.dateEvent,
                    assignedToId: data.assignedToId,
                    submit: null,
                    category: data.category,
                    subcategory: data.subcategory,
                    statusCode: data.statusCode,
                    file: null,
                    realAddress: data.realAddress === undefined ? '' : data.realAddress,
                    realAddress1: data.realAddress === undefined ? '' : data.realAddress,
                    email: data.email === undefined ? '' : data.email,
                    fullName: data.fullName,
                    phone: data.phone,
                    rut: data.rut,
                    patente: data.patente
                }}
                validationSchema={Yup.object().shape({
                    email: Yup.string().max(50, t('maximum_characters', { number: 50 })).email(t('email_validate')).required(t('The email field is required')),
                    patente: Yup.string().max(8, t('maximum_characters', { number: 8 })).matches(/^[0-9A-Za-z]+$/, t('validation_only_alpha')).required(t('This field is required')),
                    realAddress1: Yup.string().matches(/^[0-9A-Za-zñÑáéíóúÁÉÍÓÚ\-\.\,\s]+$/, t('validation_alpha_dash_space')),
                    kilometer: Yup.number().when('commune', (commune: string, schema: Yup.NumberSchema) => {

                        return commune ? schema.min(VALIDATION_RANGE_COMMUNE[commune].min, `El kilometro minimo debe ser igual o mayor que ${VALIDATION_RANGE_COMMUNE[commune].min}`).max(VALIDATION_RANGE_COMMUNE[commune].max, `El kilometro máximo es hasta ${VALIDATION_RANGE_COMMUNE[commune].max}`) : schema.min(0)
                    }),
                    phone: Yup.string().max(50, t('maximum_characters', { number: 50 })).matches(/^[0-9]+$/, 'Solo se permiten caracteres númericos')
                        .required(t('The phone field is required')),
                    sector: Yup.string().max(20, t('maximum_characters', { number: 20 })).matches(/^[a-zA-ZñÑáéíóúÁÉÍÓÚ\s]+$/, t('validation_alpha_space'))
                        .required(t('The sector field is required')),
                    commune: Yup.string().max(20, t('maximum_characters', { number: 20 })).matches(/^[a-zA-ZñÑáéíóúÁÉÍÓÚ\s]+$/, t('validation_alpha_space'))
                        .required(t('The comune field is required')),
                    fullName: Yup.string().max(100, 'La longitud maxima es de 100 caracteres').matches(/^[a-zA-ZñÑáéíóúÁÉÍÓÚ\s]+$/, t('validation_alpha_space'))
                        .required(t('The fullname field is required')),
                    rut: Yup.string().max(50, t('maximum_characters', { number: 50 })).matches(/^[0-9A-Za-z\-\.]+$/, 'Solo se permiten caracteres alfanumericos con guiones')
                        .required(t('The RUT field is required')),
                    description: Yup.string().max(300, t('maximum_characters', { number: 300 }))
                        .required(t('The description field is required')),
                    dateEvent: Yup.string()
                        .required(t('The date field is required')),
                    realAddress: Yup.string().max(100, t('maximum_characters', { number: 100 }))
                        .when(['email', 'realAddress1'], {
                            is: (email: string, realAddress1: string) => !email && !realAddress1,
                            then: Yup.string().required(t('The email physic field is required'))
                        }).matches(/^[0-9A-Za-zñÑáéíóúÁÉÍÓÚ\-\.\,\s]+$/, 'Solo se permiten caracteres alfanumericos con guiones, y espacios en blanco'),
                    category: Yup.string().required(t('The purpose event is required')),
                    orientation: Yup.string().required('La orientación es requerida'),
                    subcategory: Yup.string().required(t('El tipo de evento es requerido')),
                    classificationId: Yup.string().required(t('La clasificación es requerida')),
                }, [['email', 'realAddress1']])
                }
                onSubmit={
                    async (
                        _values,
                        { resetForm, setErrors, setStatus, setSubmitting }
                    ) => {
                        try {
                            console.log('_values', _values);
                            if (isEdit) {
                                await editEvent(_values);
                            } else {
                                await createEvent(_values);
                            }

                            resetForm();
                            setStatus({ success: true });
                            setSubmitting(true);
                        } catch (err: any) {
                            console.error('exception-event-driver', err);
                            setStatus({ success: false });
                            setErrors({ submit: err.data.error });
                            setSubmitting(false);
                            handleCreateEventMessages(err.data.error, 'error');
                        }
                    }}
            >
                {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values,
                    setFieldValue,
                }) => (
                    <form onSubmit={handleSubmit} style={{ padding: '5%' }}>
                        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                            <Grid item xs={12}>
                                <Typography variant="h4">
                                    <AccountBoxIcon /> {t('Personal Information')}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t('Full Name')}
                                    name={'fullName'}
                                    size='small'
                                    disabled={isClosed}
                                    placeholder='Max. 100 caracteres'
                                    error={Boolean(touched.fullName && errors.fullName)}
                                    helperText={touched.fullName && errors.fullName}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.fullName}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t('RUT')}
                                    name={'rut'}
                                    size='small'
                                    disabled={isClosed}
                                    error={Boolean(touched.rut && errors.rut)}
                                    helperText={touched.rut && errors.rut}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.rut}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t('Email address')}
                                    name={'email'}
                                    size='small'
                                    disabled={isClosed}
                                    error={Boolean(touched.email && errors.email)}
                                    helperText={touched.email && errors.email}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.email}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    id="phone-required"
                                    label={t('Phone')}
                                    name={'phone'}
                                    size='small'
                                    disabled={isClosed}
                                    error={Boolean(touched.phone && errors.phone)}
                                    helperText={touched.phone && errors.phone}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.phone}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl sx={{ mt: 3, width: '100%' }}>
                                    <InputLabel id="label-purpose-event">{t('Purpose Event')}</InputLabel>
                                    <Select
                                        labelId="label-purpose-event"
                                        id="purpose-event-select"
                                        label={t('Purpose event')}
                                        name={"category"}
                                        disabled={isClosed}
                                        value={values.category}
                                        error={Boolean(touched.category && errors.category)}
                                        onBlur={handleBlur}
                                        onChange={(newValue) => {
                                            setCategory(newValue.target.value);
                                            setFieldValue('category', newValue.target.value);
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>{t('Please select one')}</em>
                                        </MenuItem>
                                        {
                                            categories.map((category: CategoryResponse) => {
                                                return (
                                                    <MenuItem key={category.id} value={category.code}>{t(category.name)}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>{t('Purpose event select')}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl sx={{ mt: 3, width: '100%' }}>
                                    <InputLabel id="label-type-event">{t('Type Event')}</InputLabel>
                                    <Select
                                        labelId="label-type-event"
                                        id="type-event-select"
                                        label={t('Type event')}
                                        name={"subcategory"}
                                        disabled={isClosed}
                                        value={values.subcategory}
                                        error={Boolean(touched.subcategory && errors.subcategory)}
                                        onBlur={handleBlur}
                                        onChange={(newValue) => {
                                            setSubcategory(newValue.target.value);
                                            setFieldValue('subcategory', newValue.target.value);
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>{t('Please select one')}</em>
                                        </MenuItem>
                                        {
                                            subcategories.map((subcategory: SubCategoryResponse) => {
                                                return (
                                                    <MenuItem key={subcategory.id} value={subcategory.id}>{t(subcategory.name)}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>{t('Please select one type')}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl sx={{ mt: 1, width: '100%' }}>
                                    <InputLabel id="label-classification-event">{t('Classification')}</InputLabel>
                                    <Select
                                        labelId="label-classification-event"
                                        id="classification-event-select"
                                        name={"classificationId"}
                                        disabled={isClosed}
                                        value={values.classificationId}
                                        error={Boolean(touched.classificationId && errors.classificationId)}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        label={t('Classification')}
                                    >
                                        <MenuItem value="">
                                            <em>{t('Please select one')}</em>
                                        </MenuItem>
                                        {
                                            classifications.map((classification: ClassificationResponse) => {
                                                return (
                                                    <MenuItem key={classification.id} value={classification.id}>{t(classification.name)}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>{touched.classificationId && errors.classificationId}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl sx={{ m: 1, width: '100%' }}>
                                    <InputLabel id="label-orientation-event">{t('Orientation')}</InputLabel>
                                    <Select
                                        labelId="label-orientation-event"
                                        id="orientation-event-select"
                                        name={"orientation"}
                                        disabled={isClosed}
                                        value={values.orientation}
                                        error={Boolean(touched.orientation && errors.orientation)}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        label={t('Orientation')}
                                    >
                                        <MenuItem value="">
                                            <em>{t('Please select one')}</em>
                                        </MenuItem>
                                        <MenuItem value={"NORTH"}>{t('North to South')}</MenuItem>
                                        <MenuItem value={"SOUTH"}>{t('South to North')}</MenuItem>
                                    </Select>
                                    <FormHelperText>{touched.orientation && errors.orientation}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    id="correo-fisico-required"
                                    label={t('Email Physical')}
                                    name={"realAddress"}
                                    disabled={isClosed}
                                    fullWidth
                                    size='small'
                                    error={Boolean(touched.realAddress && errors.realAddress)}
                                    helperText={touched.realAddress && errors.realAddress}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.realAddress}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    id="patente-required"
                                    label={t('Patente')}
                                    name={"patente"}
                                    disabled={isClosed}
                                    fullWidth
                                    size='small'
                                    error={Boolean(touched.patente && errors.patente)}
                                    helperText={touched.patente && errors.patente}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.patente}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <br />
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h4">
                                    <DetailsIcon /> <b>{t('Detail Event')}</b>
                                </Typography>
                            </Grid>

                            <br />
                            <Grid item xs={6}>

                                <FormControl sx={{ m: 1, width: '100%' }}>
                                    <TextField
                                        id="sector-event"
                                        name={"sector"}
                                        value={values.sector}
                                        disabled={isClosed}
                                        error={Boolean(touched.sector && errors.sector)}
                                        helperText={touched.sector && errors.sector}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        label={t('Sector')}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl sx={{ m: 1, width: '100%' }}>
                                    <InputLabel id="label-commune-event">{t('Commune')}</InputLabel>
                                    <Select
                                        labelId="label-commune-event"
                                        id="commune-event"
                                        name={"commune"}
                                        disabled={isClosed}
                                        value={values.commune || ''}
                                        error={Boolean(touched.commune && errors.commune)}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        label={t('Commune')}
                                    >
                                        <MenuItem value="">
                                            <em>{t('Please select one')}</em>
                                        </MenuItem>
                                        {
                                            LIST_COMUMNES.map((item) => <MenuItem key={item.value} value={item.value}>{t(item.text)}</MenuItem>)
                                        }
                                    </Select>
                                    <FormHelperText>{touched.commune && errors.commune}</FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                                <FormControl sx={{ m: 1, width: '100%' }}>
                                    <TextField
                                        id="km-event"
                                        name={"kilometer"}
                                        type="number"
                                        value={values.kilometer}
                                        disabled={isClosed}
                                        error={Boolean(touched.kilometer && errors.kilometer)}
                                        helperText={touched.kilometer && errors.kilometer}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        label={t('Kilometer')}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl sx={{ m: 1, width: '100%' }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DateTimePicker
                                            label={t('Date Event')}
                                            value={values.dateEvent}
                                            disabled={isClosed || isEdit}
                                            onChange={(newValue) => {
                                                setFieldValue('dateEvent', newValue);
                                            }}
                                            maxDateTime={new Date()}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <br />
                                <Divider />
                            </Grid>
                            <Grid item xs={9}>
                                <Typography variant="h4">
                                    <DescriptionIcon /> <b>{t('Description of event')}</b>
                                </Typography>
                            </Grid>
                            {showButtonAttachment ? <Grid item xs={12}>
                                <section className="container">
                                    <div {...getRootProps({ style: dropzone as CSSProperties })}>
                                        <input {...getInputProps()}
                                            onChange={async (e) => {
                                                getInputProps().onChange?.(e);
                                                setFieldValue('file', e.target.files);
                                            }}
                                            accept=".png,.jpg,.jpeg,.PNG,.JPG,.JPEG"
                                        />
                                        <p> Solo se permiten imagenes hasta 2MB. Extensiones permitidas: png, jpg, jpeg</p>
                                        <Button variant="contained" component="span" onClick={open}>
                                            {t('Add a photo')} <AttachFileIcon />
                                        </Button>
                                    </div>
                                    <aside style={thumbsContainer as CSSProperties}>
                                        {thumbs}
                                    </aside>
                                </section>
                            </Grid> : null}

                            <Grid item xs={12}>
                                <TextEditor
                                    disabled={isClosed}
                                    setFieldValue={(val) => setFieldValue("description", val)}
                                    value={values.description}
                                />
                                {
                                    errors.description && touched.description ? (<Text color="error">{errors.description}</Text>) : null
                                }
                            </Grid>
                        </Grid>
                        <br />

                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="flex-start"
                        >
                            <Grid item xs={12}>

                                <Button
                                    startIcon={
                                        isSubmitting ? <CircularProgress size="1rem" /> : <SaveIcon fontSize="small" />
                                    }
                                    disabled={Boolean(errors.submit) || isSubmitting || isClosed}
                                    type='submit'
                                    variant='contained'
                                    sx={{ float: 'right' }}
                                >{t(titleButtonAction)}</Button>
                            </Grid>
                            <Grid item xs={6}>
                                {
                                    isEdit ? <Button
                                        startIcon={
                                            isSubmitting ? <CircularProgress size="1rem" /> : <AddTwoToneIcon fontSize="small" />
                                        }
                                        color='error'
                                        disabled={isSubmitting}
                                        type='button'
                                        variant='contained'
                                        onClick={async () => {
                                            await closedTicket(data);
                                        }}
                                    >{t('Close this event')}</Button> : null
                                }
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="flex-start"
                        >

                        </Grid>
                    </form>
                )}
            </Formik >
        );
    } else {
        return (
            <>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        width: '100%',
                        height: '100%'
                    }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CircularProgress size={64} disableShrink thickness={3} />
                </Box>
            </>
        )
    }
}

export default FormEvent;